import {
  type DeleteItemSetSubQuestionWorkflowMutationOptions,
  GetQuestionCreationTaskByIdDocument,
  type GetTaskQuestionGroupByIdQuery,
  useDeleteItemSetSubQuestionWorkflowMutation,
  useGetTaskQuestionGroupByIdQuery,
} from '@exam-management/graphql';
import { ErrorBox, Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useCallback, useMemo } from 'react';

type QuestionGroup = GetTaskQuestionGroupByIdQuery['qctGetTaskQuestionGroupById'];

export interface ItemSetQuestionDetailsContextData {
  itemSet: QuestionGroup;
  isDeleting: boolean;
  deleteSubQuestion: (
    subQuestionId: string,
    options?: DeleteItemSetSubQuestionWorkflowMutationOptions,
  ) => Promise<void>;
}

interface ItemSetQuestionDetailsProviderProps extends PropsWithChildren {
  taskId: string;
  questionGroupId: string;
}

export const ItemSetQuestionDetailsContext = createContext<
  ItemSetQuestionDetailsContextData | undefined
>(undefined);

export default function ItemSetQuestionDetailsProvider(
  props: Readonly<ItemSetQuestionDetailsProviderProps>,
) {
  const { taskId, questionGroupId, children } = props;

  const {
    data: itemSetQuestionData,
    loading: isLoading,
    error: getQuestionGroupError,
  } = useGetTaskQuestionGroupByIdQuery({
    variables: {
      questionGroupId,
      taskId,
    },
  });

  const [deleteItemSetSubQuestionWorkflowMutation, { loading: isDeleting }] =
    useDeleteItemSetSubQuestionWorkflowMutation();

  const deleteSubQuestion = useCallback(
    async (subQuestionId: string, options?: DeleteItemSetSubQuestionWorkflowMutationOptions) => {
      const itemSetId = itemSetQuestionData?.qctGetTaskQuestionGroupById.id;

      if (!itemSetId) return;

      await deleteItemSetSubQuestionWorkflowMutation({
        variables: {
          input: {
            itemSetId,
            subQuestionId,
          },
          taskId,
        },
        ...options,
        awaitRefetchQueries: true,
        refetchQueries: [GetQuestionCreationTaskByIdDocument],
      });
    },
    [deleteItemSetSubQuestionWorkflowMutation, itemSetQuestionData, taskId],
  );

  const contextValue = useMemo(() => {
    return {
      itemSet: itemSetQuestionData?.qctGetTaskQuestionGroupById as QuestionGroup,
      isDeleting,
      deleteSubQuestion,
    };
  }, [itemSetQuestionData, isDeleting, deleteSubQuestion]);

  if (isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  if (getQuestionGroupError) {
    return <ErrorBox />;
  }

  return (
    <ItemSetQuestionDetailsContext.Provider value={contextValue}>
      {children}
    </ItemSetQuestionDetailsContext.Provider>
  );
}
